import React, {
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react"
import i18nData from "@rogue_4/locales/zh-CN.json"
import anime from "animejs"
import classNames from "classnames"

import { CommonTransition } from "@/topic/rogue_4/components/CommonTransition"
import { ESubPage, GlobalContext } from "@/topic/rogue_4/context"
import { battleMusic, gameMusic } from "@/topic/rogue_4/utils/music"
import { i18n } from "@/utils/i18n"
import ANIMATION from "../../animation"
import Button from "../../components/Button"
import GameCharacter, { ICharacterRef } from "../../components/Character"
import CharacterBoard from "../../components/CharacterBoard"
import CircleButton from "../../components/CircleButton"
import DialogFrame, {
    DialogContent,
    DialogTitle,
    richTextToHtml,
} from "../../components/Dialog"
import GAME_EMITTER from "../../components/Emitter"
import HealthBoss from "../../components/HealthBoss"
import {
    useGameBackpackModal,
    useGameGalleryModal,
    useGameHistoryModal,
    useGameRestartModal,
    useGameUseItemModal,
} from "../../components/Modal/store"
import {
    FAIL_PERFORMANCE,
    GAME_EQUIP_DICT,
    getKaltsCheckBuffText,
    getKaltsSummaryText,
    getNymphSummaryText,
    IGameEquipWithAction,
    IKaltsAction,
    IPerformance,
    KILL_PERFORMANCE,
    rollKaltsAction,
    SPECIAL_PERFORMANCE,
    useGameBattle,
    WIN_PERFORMANCE,
} from "../../data/battle"
import CHARACTER_DICT from "../../data/character"
import dialogJson from "../../data/json/dialogBoss.json"
import { useDivRef } from "../../hooks/useDivRef"
import { useEvent } from "../../hooks/useEvent"
import { useSingleEase } from "../../hooks/useSingleEase"
import { useGameSetResult, useGameStore } from "../../store"
import { IAnimateRef, THistory } from "../../type"
import { SFX } from "../../utils/sound"
import { useNextScene } from "../store"

import ImgBoss1 from "../../../../assets/img/game/dialog/qb1.png"
import ImgBoss2 from "../../../../assets/img/game/dialog/qb2.png"
import styles from "./index.module.less"

const DialogBossScene = React.forwardRef<IAnimateRef>((_, ref) => {
    const nextScene = useNextScene()

    // main frame control
    const [mainFrameActive, setMainFrameActive] = useState(false)
    const [mainBg, setMainBg] = useState(ImgBoss1)

    // main content control
    const [mainTitleActive, setMainTitleActive] = useState(false)
    const [mainContentActive, setMainContentActive] = useState(false)
    const contentRef = useSingleEase(mainContentActive, 500, 500)
    const [text, setText] = useState("")
    const lockNextRef = useRef<boolean>(false)
    const typeEndCbRef = useRef<() => void>()
    const onTypeEnd = useCallback(() => {
        lockNextRef.current = false
        typeEndCbRef.current?.()
    }, [])
    const changeText = useCallback(
        (value: string, cb?: () => void) => {
            if (text === value) {
                return
            } else {
                typeEndCbRef.current = cb
                lockNextRef.current = true
                setText(value)
            }
        },
        [text]
    )

    // character control
    const [leftActive, setLeftActive] = useState(false)
    const [rightActive, setRightActive] = useState(false)
    const [leftGray, setLeftGray] = useState(false)
    const [rightGray, setRightGray] = useState(false)
    const [rightCharacter, setRightCharacter] = useState("hraaselsuher")
    const leftCharacterRef = useRef<ICharacterRef>(null)
    const rightCharacterRef = useRef<ICharacterRef>(null)

    const battleHistory = useRef<THistory[]>([])

    // useItem button
    const [buttonActive, setButtonActive] = useState(false)
    const [healthBarActive, setHealthBarActive] = useState(false)

    // modal button
    const callBackpackModal = useGameBackpackModal()
    const callGalleryModal = useGameGalleryModal()
    const callHistoryModal = useGameHistoryModal()
    const callRestartModal = useGameRestartModal()

    const handleRestart = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            SFX.play("commonClick")
            callRestartModal()
        },
        [callRestartModal]
    )
    const handleHistory = useCallback(
        (e: React.MouseEvent) => {
            SFX.play("commonClick")
            e.stopPropagation()
            callHistoryModal(battleHistory.current)
        },
        [callHistoryModal]
    )

    // 0 default, 1 initialized
    const [currentStep, setCurrentStep] = useState(0)
    const lockRef = useRef(false)
    const firstStepContents = dialogJson
    const indexRef = useRef(0)

    const {
        state: { bgm, currentSubPage },
    } = useContext(GlobalContext)

    // init
    const handleFirstStep = useCallback(() => {
        if (lockRef.current) return
        lockRef.current = true
        waitChoiceRef.current = true
        setCurrentStep(1)
        setMainFrameActive(false)
        setRightActive(false)
        setMainTitleActive(false)
        setMainContentActive(false)

        useGameStore.setState({
            battleMusic: true,
        })
        try {
            gameMusic.disable()
            if (bgm) {
                battleMusic.enable()
            }
        } catch (error) {
            console.error(error)
        }

        setTimeout(() => {
            setMainBg(ImgBoss2)
            setMainFrameActive(true)
            setRightCharacter("kalts")
            setRightActive(true)
            setTimeout(() => {
                lockRef.current = false
                setHealthBarActive(true)
                const text = i18n(
                    i18nData,
                    "rogue_4.game.dialogBoss.battle.choice"
                )
                battleHistory.current.push({
                    character: "BATTLE",
                    description: richTextToHtml(text),
                })
                changeText(text, () => {
                    setButtonActive(true)
                })
                setMainContentActive(true)
            }, 1000)
        }, 1000)
    }, [bgm, changeText])
    const handleFirstNext = useCallback(() => {
        indexRef.current = indexRef.current + 1
        const text = firstStepContents[indexRef.current]
        if (!text) handleFirstStep()
        else {
            battleHistory.current.push({
                character: "BATTLE",
                description: richTextToHtml(text),
            })
            changeText(text)
        }
    }, [changeText, firstStepContents, handleFirstStep])

    // battle
    const {
        flags,
        kaltsDebuff,
        nymphRate,
        nymphHealth,
        nymphShield,
        kaltsHealth,
        kaltsShield,
        goKalts,
        goNymph,
        checkKaltsBuff,
    } = useGameBattle()

    const waitChoiceRef = useRef(false)
    // 0~6  0,2,4 for nymph  1,3,5 fro kalts
    const turnRef = useRef(0)
    const currentNymphActionRef = useRef<IGameEquipWithAction>()
    const currentKaltsActionRef = useRef<IKaltsAction>()
    const performanceList = useRef<IPerformance[]>([])
    const currentPerformanceIndex = useRef<number>(0)
    const setResult = useGameSetResult()
    const endRef = useRef(false)
    const handleSpecialEnd = useCallback(() => {
        setResult({
            key: "03",
            health: { kalts: kaltsHealth, nymph: nymphHealth },
        })
        nextScene()
    }, [kaltsHealth, nextScene, nymphHealth, setResult])
    const handleEndGame = useCallback(() => {
        if (nymphHealth >= kaltsHealth) {
            setResult({
                key: "01",
                health: { kalts: kaltsHealth, nymph: nymphHealth },
            })
        } else {
            setResult({
                key: "02",
                health: { kalts: kaltsHealth, nymph: nymphHealth },
            })
        }
        nextScene()
    }, [kaltsHealth, nextScene, nymphHealth, setResult])
    const handleNextTurn = useEvent(() => {
        turnRef.current = turnRef.current + 1
        if (turnRef.current % 2 === 0) {
            // nymph's turn
            currentPerformanceIndex.current = 0
            performanceList.current = []
            waitChoiceRef.current = true
            changeText(
                i18n(i18nData, "rogue_4.game.dialogBoss.battle.choice"),
                () => {
                    setButtonActive(true)
                }
            )
        } else {
            // kalts's turn
            const action = rollKaltsAction(
                turnRef.current === 5,
                kaltsHealth === 10
            )
            currentPerformanceIndex.current = 0
            const l = [...action.performance]

            if (kaltsDebuff) {
                l.push({ text: "CHECK_BUFF" })
            }
            currentKaltsActionRef.current = action
            performanceList.current = l
        }
        handleBattleNextStep()
    })
    const specialEndFlag = useRef(false)
    const handleBattleNextStep = useEvent(() => {
        if (lockNextRef.current) return
        if (waitChoiceRef.current) return

        if (kaltsHealth === 0 && endRef.current === false) {
            endRef.current = true
            performanceList.current.push(...KILL_PERFORMANCE)
        }
        let performance =
            performanceList.current[currentPerformanceIndex.current]

        let nextText = ""
        if (!performance) {
            if (kaltsHealth === 0) {
                handleEndGame()
                return
            } else if (turnRef.current === 4 && specialEndFlag.current) {
                handleSpecialEnd()
                return
            } else if (turnRef.current < 5) {
                handleNextTurn()
                return
            } else {
                if (!endRef.current) {
                    endRef.current = true
                    nymphHealth >= kaltsHealth
                        ? performanceList.current.push(...WIN_PERFORMANCE)
                        : performanceList.current.push(...FAIL_PERFORMANCE)
                    performance =
                        performanceList.current[currentPerformanceIndex.current]
                } else {
                    handleEndGame()
                    return
                }
            }
        }
        if (performance.text === "SUMMARY") {
            if (turnRef.current % 2 === 0) {
                const result = goNymph(currentNymphActionRef.current!.base)
                // kalts
                let sfx = false
                if (result.damage) {
                    rightCharacterRef.current?.hurt()
                    if (!sfx) {
                        SFX.play("hurt")
                        sfx = true
                    }
                } else if (
                    result.buff ||
                    result.flags.kaltsAttackDisabled ||
                    result.flags.kaltsHealDisabled
                ) {
                    rightCharacterRef.current?.shake()
                    if (!sfx) {
                        SFX.play("debuff")
                        sfx = true
                    }
                }
                // nymph
                if (result.heal) {
                    leftCharacterRef.current?.heal()
                    if (!sfx) {
                        SFX.play("heal")
                        sfx = true
                    }
                } else if (result.shield) {
                    leftCharacterRef.current?.shield()
                    if (!sfx) {
                        SFX.play("shield")
                        sfx = true
                    }
                } else if (result.double) {
                    if (!sfx) {
                        SFX.play("buff")
                        sfx = true
                    }
                }
                nextText = getNymphSummaryText(result)
            } else {
                const result = goKalts(currentKaltsActionRef.current!.base)
                let sfx = false
                // nymph
                if (result.damage) {
                    leftCharacterRef.current?.hurt()
                    if (!sfx) {
                        SFX.play("hurt")
                        sfx = true
                    }
                } else if (
                    result.flags.nymphAttackDisabled ||
                    result.flags.nymphHealGuardDisabled
                ) {
                    leftCharacterRef.current?.shake()
                    if (!sfx) {
                        SFX.play("debuff")
                        sfx = true
                    }
                }
                // kalts
                if (result.heal) {
                    rightCharacterRef.current?.heal()
                    if (!sfx) {
                        SFX.play("heal")
                        sfx = true
                    }
                } else if (result.shield) {
                    rightCharacterRef.current?.shield()
                    if (!sfx) {
                        SFX.play("shield")
                        sfx = true
                    }
                }
                nextText = getKaltsSummaryText(result)
            }
        } else if (performance.text === "CHECK_BUFF") {
            const damage = checkKaltsBuff()
            nextText = getKaltsCheckBuffText(damage)
        } else {
            nextText = performance.text
        }
        changeText(nextText)
        battleHistory.current.push({
            character: "BATTLE",
            description: richTextToHtml(nextText),
        })
        // animation
        if (performance.animation) {
            if (performance.animation.kalts) {
                rightCharacterRef.current?.[performance.animation.kalts]?.()
            }
            if (performance.animation.nymph) {
                leftCharacterRef.current?.[performance.animation.nymph]?.()
            }
        }
        currentPerformanceIndex.current = currentPerformanceIndex.current + 1
    })
    const handleUseItem = useCallback(
        (key: string) => {
            currentPerformanceIndex.current = 0
            if (turnRef.current === 4 && GAME_EQUIP_DICT[key].base.special) {
                specialEndFlag.current = true
                performanceList.current = SPECIAL_PERFORMANCE
            } else {
                performanceList.current = GAME_EQUIP_DICT[key].performance
            }
            currentNymphActionRef.current = GAME_EQUIP_DICT[key]
            waitChoiceRef.current = false

            setButtonActive(false)
            handleBattleNextStep()
        },
        [handleBattleNextStep]
    )

    // next
    const handleNext = useCallback(() => {
        if (lockNextRef.current) return
        if (currentStep === 0) {
            handleFirstNext()
        } else if (currentStep === 1) {
            handleBattleNextStep()
        }
    }, [currentStep, handleBattleNextStep, handleFirstNext])

    // bind event
    useEffect(() => {
        const cb = () => handleNext()
        // FIXME remove this
        // const cb = () => {
        //     setResult({
        //         key: "01",
        //         health: { kalts: 6, nymph: 6 },
        //     })
        //     nextScene()
        // }
        GAME_EMITTER.on("NEXT", cb)
        return () => GAME_EMITTER.off("NEXT", cb)
    }, [handleNext, nextScene, setResult])

    const bgRef = useDivRef()
    const leftCardRef = useDivRef()
    const rightCardRef = useDivRef()
    const buttonsRef = useDivRef()
    const dialogFrameRef = useDivRef()
    const dialogContentRef = useDivRef()
    const bottomButtonRef = useDivRef()
    const healthBarRef = useDivRef()
    const buffBarRef = useDivRef()
    //需改回
    // useImperativeHandle(
    //     ref,

    //     () => ({
    //         fadeIn: async () => {
    //             setMainFrameActive(true)
    //             const inst = anime.timeline()

    //             inst.add(
    //                 ANIMATION.in(
    //                     bgRef,
    //                     {},
    //                     {
    //                         duration: 10,
    //                     }
    //                 ),
    //                 50
    //             )
    //             inst.add(
    //                 ANIMATION.in(
    //                     [
    //                         dialogFrameRef,
    //                         dialogContentRef,
    //                         leftCardRef,
    //                         rightCardRef,
    //                     ],
    //                     {},
    //                     { duration: 10, scale: [0.6, 1] }
    //                 ),
    //                 150
    //             )
    //             inst.add(
    //                 {
    //                     begin: () => {
    //                         setTimeout(() => {
    //                             lockRef.current = false
    //                         }, 500)
    //                         setMainTitleActive(true)
    //                         setMainContentActive(true)
    //                         setLeftActive(true)
    //                         setRightActive(true)
    //                         changeText(dialogJson[0])
    //                         battleHistory.current.push({
    //                             character: "BATTLE",
    //                             description: richTextToHtml(dialogJson[0]),
    //                         })
    //                     },
    //                 },
    //                 200
    //             )
    //                 .add(
    //                     {
    //                         begin: () => {},
    //                     },
    //                     260
    //                 )
    //                 .add(
    //                     ANIMATION.in(
    //                         [buttonsRef, bottomButtonRef],
    //                         {},
    //                         { duration: 10 }
    //                     ),
    //                     30
    //                 )
    //             return inst.finished
    //         },
    //         fadeOut: async () => {
    //             const inst = anime.timeline()

    //             inst.add(
    //                 ANIMATION.out(
    //                     bgRef,
    //                     {},
    //                     {
    //                         duration: 10,
    //                         begin: () => {
    //                             useGameStore.setState({
    //                                 battleMusic: false,
    //                             })
    //                             if (bgm && currentSubPage === ESubPage.Game) {
    //                                 battleMusic.disable()
    //                                 gameMusic.enable()
    //                             }
    //                         },
    //                     }
    //                 ),
    //                 50
    //             )
    //                 .add(
    //                     ANIMATION.out(
    //                         [
    //                             buttonsRef,
    //                             bottomButtonRef,
    //                             healthBarRef,
    //                             buffBarRef,
    //                         ],
    //                         {},
    //                         { duration: 5 }
    //                     ),
    //                     0
    //                 )
    //                 .add(
    //                     ANIMATION.out(
    //                         [
    //                             dialogFrameRef,
    //                             dialogContentRef,
    //                             leftCardRef,
    //                             rightCardRef,
    //                         ],
    //                         {},
    //                         { duration: 10 }
    //                     ),
    //                     0
    //                 )

    //                 .add(
    //                     {
    //                         begin: () => {
    //                             // setMainContentActive(true)
    //                             setLeftActive(false)
    //                             setRightActive(false)
    //                         },
    //                     },
    //                     0
    //                 )
    //             // inst.finished.then(() => console.log("finish"))
    //             return inst.finished
    //         },
    //     }),
    //     [
    //         bgRef,
    //         bgm,
    //         bottomButtonRef,
    //         buffBarRef,
    //         buttonsRef,
    //         changeText,
    //         currentSubPage,
    //         dialogContentRef,
    //         dialogFrameRef,
    //         healthBarRef,
    //         leftCardRef,
    //         rightCardRef,
    //     ]
    // )
    useImperativeHandle(
        ref,

        () => ({
            fadeIn: async () => {
                setMainFrameActive(true)
                const inst = anime.timeline()

                inst.add(
                    ANIMATION.in(
                        bgRef,
                        {},
                        {
                            duration: 1000,
                        }
                    ),
                    500
                )
                inst.add(
                    ANIMATION.in(
                        [
                            dialogFrameRef,
                            dialogContentRef,
                            leftCardRef,
                            rightCardRef,
                        ],
                        {},
                        { duration: 1000, scale: [0.6, 1] }
                    ),
                    1500
                )
                inst.add(
                    {
                        begin: () => {
                            setTimeout(() => {
                                lockRef.current = false
                            }, 500)
                            setMainTitleActive(true)
                            setMainContentActive(true)
                            setLeftActive(true)
                            setRightActive(true)
                            changeText(dialogJson[0])
                            battleHistory.current.push({
                                character: "BATTLE",
                                description: richTextToHtml(dialogJson[0]),
                            })
                        },
                    },
                    2000
                )
                    .add(
                        {
                            begin: () => {},
                        },
                        2600
                    )
                    .add(
                        ANIMATION.in(
                            [buttonsRef, bottomButtonRef],
                            {},
                            { duration: 1000 }
                        ),
                        3000
                    )
                return inst.finished
            },
            fadeOut: async () => {
                const inst = anime.timeline()

                inst.add(
                    ANIMATION.out(
                        bgRef,
                        {},
                        {
                            duration: 1000,
                            begin: () => {
                                useGameStore.setState({
                                    battleMusic: false,
                                })
                                if (bgm && currentSubPage === ESubPage.Game) {
                                    battleMusic.disable()
                                    gameMusic.enable()
                                }
                            },
                        }
                    ),
                    500
                )
                    .add(
                        ANIMATION.out(
                            [
                                buttonsRef,
                                bottomButtonRef,
                                healthBarRef,
                                buffBarRef,
                            ],
                            {},
                            { duration: 500 }
                        ),
                        0
                    )
                    .add(
                        ANIMATION.out(
                            [
                                dialogFrameRef,
                                dialogContentRef,
                                leftCardRef,
                                rightCardRef,
                            ],
                            {},
                            { duration: 1000 }
                        ),
                        0
                    )

                    .add(
                        {
                            begin: () => {
                                // setMainContentActive(true)
                                setLeftActive(false)
                                setRightActive(false)
                            },
                        },
                        0
                    )
                // inst.finished.then(() => console.log("finish"))
                return inst.finished
            },
        }),
        [
            bgRef,
            bgm,
            bottomButtonRef,
            buffBarRef,
            buttonsRef,
            changeText,
            currentSubPage,
            dialogContentRef,
            dialogFrameRef,
            healthBarRef,
            leftCardRef,
            rightCardRef,
        ]
    )

    const callUseItemModal = useGameUseItemModal()
    const handleCallUseItemModal = useCallback(() => {
        SFX.play("commonClick")
        if (lockRef.current) return
        callUseItemModal(handleUseItem)
    }, [callUseItemModal, handleUseItem])

    const gameComplete = useGameStore((state) => state.gameComplete)
    const lockedGameComplete = useMemo(() => gameComplete, []) // this dependency is wrong, but it's designed for this
    return (
        <div className={styles.dialogBossScene}>
            <div className={styles.totalBg} ref={bgRef}></div>
            <DialogFrame
                ref={dialogFrameRef}
                className={styles.mainDialog}
                active={mainFrameActive}
                bg={mainBg}
            ></DialogFrame>
            <CharacterBoard
                className={styles.leftBoard}
                ref={leftCardRef}
                type="left"
                characterKey="nymph"
            />
            <CharacterBoard
                className={styles.rightBoard}
                ref={rightCardRef}
                type="right"
                characterKey={"kalts"}
            />
            <GameCharacter
                gray={leftGray}
                character={CHARACTER_DICT["nymph"]}
                active={leftActive}
                ref={leftCharacterRef}
            />
            <GameCharacter
                gray={rightGray}
                character={CHARACTER_DICT[rightCharacter]}
                active={rightActive}
                ref={rightCharacterRef}
            />

            <div className={styles.mainContent} ref={dialogContentRef}>
                <DialogTitle
                    className={styles.title}
                    active={mainTitleActive}
                    text="? ? ?"
                ></DialogTitle>
                <DialogContent
                    className={classNames(
                        styles.content,
                        rightCharacter === "kalts" && styles.fullContent
                    )}
                    text={text}
                    onTypeEnd={onTypeEnd}
                    ref={contentRef}
                ></DialogContent>
            </div>
            <div className={styles.circleButtons} ref={buttonsRef}>
                <CircleButton
                    type="backpack"
                    className={styles.buttonBackpack}
                    onClick={() => {
                        SFX.play("commonClick")
                        callBackpackModal()
                    }}
                />
                {lockedGameComplete && (
                    <CircleButton
                        type="gallery"
                        className={styles.buttonGallery}
                        onClick={() => {
                            SFX.play("commonClick")
                            callGalleryModal()
                        }}
                    />
                )}
            </div>
            <CommonTransition state={`${buttonActive}`}>
                <div
                    className={classNames(
                        styles.choiceButtonBar,
                        styles.active
                    )}
                >
                    {buttonActive && (
                        <Button
                            className={styles.button}
                            active={true}
                            onClick={handleCallUseItemModal}
                        >
                            {i18n(i18nData, "rogue_4.game.useItem")}
                        </Button>
                    )}
                </div>
            </CommonTransition>

            <div className={styles.buttonBar} ref={bottomButtonRef}>
                <div
                    className={classNames(styles.button, styles.restart)}
                    onClick={handleRestart}
                ></div>
                <div
                    className={classNames(styles.button, styles.history)}
                    onClick={handleHistory}
                ></div>
            </div>
            <div
                ref={healthBarRef}
                className={classNames(
                    styles.healthBars,
                    healthBarActive && styles.active
                )}
            >
                <HealthBoss
                    className={styles.left}
                    health={nymphHealth}
                    shield={nymphShield}
                />
                <HealthBoss
                    className={styles.right}
                    health={kaltsHealth}
                    shield={kaltsShield}
                    kalts
                />
            </div>
            <div
                ref={buffBarRef}
                className={classNames(
                    styles.buffBars,
                    healthBarActive && styles.active
                )}
            >
                <div className={styles.left}>
                    {flags.nymphAttackDisabled && (
                        <div
                            className={classNames(styles.buff, styles.noAttack)}
                        ></div>
                    )}
                    {flags.nymphHealGuardDisabled && (
                        <div
                            className={classNames(styles.buff, styles.noHeal)}
                        ></div>
                    )}
                    {nymphRate > 1 && (
                        <div
                            className={classNames(
                                styles.buff,
                                styles[
                                    nymphRate >= 8
                                        ? "double3"
                                        : nymphRate >= 4
                                          ? "double2"
                                          : "double1"
                                ]
                            )}
                        ></div>
                    )}
                </div>
                <div className={styles.right}>
                    {flags.kaltsAttackDisabled && (
                        <div
                            className={classNames(styles.buff, styles.noAttack)}
                        ></div>
                    )}
                    {flags.kaltsHealDisabled && (
                        <div
                            className={classNames(styles.buff, styles.noHeal)}
                        ></div>
                    )}
                    {flags.kaltsTaunt && (
                        <div
                            className={classNames(styles.buff, styles.taunt)}
                        ></div>
                    )}
                    {kaltsDebuff && (
                        <div
                            className={classNames(
                                styles.buff,
                                styles[`debuff${kaltsDebuff}`]
                            )}
                        ></div>
                    )}
                </div>
            </div>
        </div>
    )
})
DialogBossScene.displayName = "DialogBossScene"

export default DialogBossScene
